import { useCallback, useEffect, useState } from "react";
import {
  addSeats,
  isEnded,
  previewSeats,
  SUBSCRIPTION_ENDED,
  type BillingContext,
  type SeatsPreview,
} from "@/lib/api/organizations";
import { QUOTE_SETTLE_MS } from "@/lib/paddle";

/// Paddle refuses an update to a subscription with a pending change, so a
/// cancelling or paused one cannot grow.
export function canBuySeats(billing: BillingContext | null): boolean {
  const subscription = billing?.subscription;
  return Boolean(
    billing?.can_manage_billing &&
    billing.processor_customer_id &&
    subscription &&
    subscription.status !== "paused" &&
    subscription.scheduled_change?.action !== "cancel",
  );
}

/// Buying alone floors at the higher of what is paid for and what is occupied;
/// with `allowDecrease` a paid seat may be given up, so only the roster holds.
export function useSeatPurchase(
  orgId: string,
  held: number,
  {
    occupied = 0,
    allowDecrease = false,
    onEnded,
  }: {
    occupied?: number;
    allowDecrease?: boolean;
    /// Paddle said the subscription had ended: whatever read the billing re-reads.
    onEnded: () => void;
  },
) {
  const floor = allowDecrease
    ? Math.max(occupied, 1)
    : Math.max(held, occupied);
  // A two-way editor opens where you are; an add-only one opens on the first
  // number worth confirming.
  const start = allowDecrease && floor < held ? held : floor + 1;
  const [open, setOpen] = useState(false);
  const [wanted, setWanted] = useState(start);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    seats: number;
    figures: SeatsPreview | null;
  }>();
  const changing = open && wanted !== held;

  useEffect(() => {
    if (!changing) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void previewSeats(orgId, wanted, controller.signal).then((figures) => {
        if (controller.signal.aborted) return;
        if (isEnded(figures)) {
          setPreview({ seats: wanted, figures: null });
          onEnded();
          return;
        }
        setPreview({ seats: wanted, figures });
      });
    }, QUOTE_SETTLE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [changing, orgId, wanted, onEnded]);

  const openEditor = useCallback(() => {
    setWanted(start);
    setError(null);
    // Proration moves by the day, so an earlier opening's figures are stale.
    setPreview(undefined);
    setOpen(true);
  }, [start]);

  const close = useCallback(() => setOpen(false), []);

  /// The count Paddle now holds, or `null` when nothing moved.
  const confirm = useCallback(async (): Promise<number | null> => {
    setBusy(true);
    setError(null);
    const result = await addSeats(orgId, wanted);
    setBusy(false);
    if (isEnded(result)) {
      setError(SUBSCRIPTION_ENDED);
      onEnded();
      return null;
    }
    if (!result) {
      setError(
        wanted < held
          ? "The seat change could not be saved. Please try again."
          : "The seats could not be added. If a charge was declined, update your card and try again.",
      );
      return null;
    }
    setOpen(false);
    return result.seats;
  }, [orgId, wanted, held, onEnded]);

  return {
    open,
    openEditor,
    close,
    floor,
    wanted,
    setWanted,
    /// Paddle's figures for `wanted`; `"quoting"` until they land.
    preview: !changing
      ? null
      : preview?.seats === wanted
        ? preview.figures
        : ("quoting" as const),
    busy,
    error,
    confirm,
  };
}
